import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { hasLeadingBodytextLedd } from '~/utils/solr/law-utils'
import { UNKNOWN_LAW_NODE_TYPE } from '~/utils/solr/law-node-type'

// Some sections store the first ledd in the section body text.
// Add a TOC entry for it and link to the section where the text renders.
function firstLeddTocNode(section: LawAndCommentary): LawAndCommentary {
  return {
    ...section,
    nodeType: 'subsection',
    title: undefined,
    children: undefined,
    ezMainNodeId: undefined,
  }
}

/**
 * TOC entries for a node.
 *
 * If a section shows its body text as the first ledd,
 * add a fake first ledd entry before the child entries.
 */
export function buildTocChildren(node: LawAndCommentary, isStandardText: boolean): LawAndCommentary[] {
  const children = node.children?.filter(c => c.nodeType !== 'footnote' && c.nodeType !== 'note' && c.nodeType !== UNKNOWN_LAW_NODE_TYPE) ?? []

  // NS standards have deeper nesting, so keep their children unchanged
  if (isStandardText) {
    return children
  }

  if (hasLeadingBodytextLedd(node, isStandardText)) {
    return [firstLeddTocNode(node), ...children]
  }

  // A single subsection is shown inline under the section, so it does not need its own TOC entry
  const subsectionChildren = children.filter(c => c.nodeType === 'subsection')
  if (children.length === 1 && subsectionChildren.length === 1) {
    return []
  }

  return children
}

export function collectSectionLabels(node: LawAndCommentary) {
  const sectionLabels: string[] = []

  function traverseNode(node: LawAndCommentary) {
    const fragmentId = node.id
    if (node.nodeType === 'section') {
      // Only numbered sections get a label. Unnumbered sections (e.g. a
      // "Slutningsbestemmelse") have positional ids like "children#0",
      // which must not leak into the TOC.
      const match = fragmentId.match(/paragraf-([^/]+)/i)
      if (match?.[1]) {
        sectionLabels.push(match[1])
      }
    }
    if (node.children) {
      for (const child of node.children) {
        traverseNode(child)
      }
    }
  }

  traverseNode(node)
  return sectionLabels
}
